import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { AuthProvider, useAuth } from './context/AuthContext';
import { UserProvider, useUser } from './context/UserContext';
import { ThemeProvider } from './context/ThemeContext';
import { NotificationProvider } from './context/NotificationContext';
import { isProfileComplete } from './utils/profileUtils';
import LoadingSpinner from './components/LoadingSpinner';
import bgImage from './assets/img/partyland-bg-infinite.jpg';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ModeSelectPage from './pages/ModeSelectPage';
import InitializeCredentialPage from './pages/InitializeCredentialPage';
import CredentialBuilderPage from './pages/CredentialBuilderPage';
import GuestDashboardPage from './pages/GuestDashboardPage';
import HostDashboardPage from './pages/HostDashboardPage';
import EventCreatePage from './pages/EventCreatePage';
import EventSharePage from './pages/EventSharePage';
import EventOperationPage from './pages/EventOperationPage';
import EventApplyPage from './pages/EventApplyPage';
import PaymentRecoveryPage from './pages/PaymentRecoveryPage';
import UserProfilePage from './pages/UserProfilePage';
import WalletPage from './pages/WalletPage';
import SubscriptionPage from './pages/SubscriptionPage';
import NotificationsPage from './pages/NotificationsPage';
import FriendRequestsPage from './pages/FriendRequestsPage';

import './index.css';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Redirect to dashboard if already authenticated
function PublicRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();
  const { profile } = useUser();

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (isAuthenticated) {
    // No profile at all - show intro page
    if (!profile) {
      return <Navigate to="/initialize-credential" replace />;
    }
    // Profile exists but incomplete - go directly to builder (skip intro)
    if (!isProfileComplete(profile)) {
      return <Navigate to="/credential-builder" replace />;
    }
    // Profile is complete - go to dashboard
    if (profile.initialMode === 'host') {
      return <Navigate to="/host" replace />;
    }
    return <Navigate to="/guest" replace />;
  }

  return children;
}

// Redirects to dashboard if profile is already complete (for onboarding pages)
function RequireIncompleteProfile({ children }) {
  const { profile, isLoading } = useUser();

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  // Profile is complete - redirect to dashboard
  if (isProfileComplete(profile)) {
    if (profile.initialMode === 'host') {
      return <Navigate to="/host" replace />;
    }
    return <Navigate to="/guest" replace />;
  }

  return children;
}

// Requires profile to be set up
function RequireProfile({ children }) {
  const { profile, isLoading } = useUser();

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!profile) {
    return <Navigate to="/initialize-credential" replace />;
  }

  // Profile exists but incomplete - redirect to builder
  if (!isProfileComplete(profile)) {
    return <Navigate to="/credential-builder" replace />;
  }

  return children;
}

function AppRoutes() {

  return (
    <Routes>
      {/* Public routes (outside theme) */}
      <Route
        path="/"
        element={
          <PublicRoute>
            <LandingPage />
          </PublicRoute>
        }
      />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Onboarding routes (protected but no profile required) */}
      <Route
        path="/mode-select"
        element={
          <ProtectedRoute>
            <ModeSelectPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/initialize-credential"
        element={
          <ProtectedRoute>
            <RequireIncompleteProfile>
              <InitializeCredentialPage />
            </RequireIncompleteProfile>
          </ProtectedRoute>
        }
      />
      <Route
        path="/credential-builder"
        element={
          <ProtectedRoute>
            <CredentialBuilderPage />
          </ProtectedRoute>
        }
      />

      {/* Guest routes (inside theme) */}
      <Route
        path="/guest"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <GuestDashboardPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />

      {/* Host routes (inside theme) */}
      <Route
        path="/host"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <HostDashboardPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      <Route
        path="/host/events/create"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <EventCreatePage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      <Route
        path="/host/events/:id"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <EventOperationPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      <Route
        path="/host/events/:id/edit"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <EventCreatePage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      <Route
        path="/host/events/:id/share"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <EventSharePage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      <Route
        path="/host/stripe-return"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <HostDashboardPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />
      <Route
        path="/host/stripe-refresh"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <HostDashboardPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />

      {/* Event application routes */}
      <Route path="/events/:inviteCode" element={<EventApplyPage />} />
      <Route path="/e/:inviteCode" element={<EventApplyPage />} />

      {/* Payment recovery */}
      <Route
        path="/recover-payment/:applicationId"
        element={
          <ProtectedRoute>
            <PaymentRecoveryPage />
          </ProtectedRoute>
        }
      />

      {/* User profile */}
      <Route
        path="/users/:userId"
        element={
          <ProtectedRoute>
            <UserProfilePage />
          </ProtectedRoute>
        }
      />

      {/* Wallet */}
      <Route
        path="/wallet"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <WalletPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />

      {/* Subscription */}
      <Route
        path="/pro"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <SubscriptionPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />

      {/* Notifications */}
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <NotificationsPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />

      {/* Friends */}
      <Route
        path="/friends"
        element={
          <ProtectedRoute>
            <RequireProfile>
              <FriendRequestsPage />
            </RequireProfile>
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to landing */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
    const bgStyle = 'url(' + bgImage + ')';

  return (
    <Router>
      <ThemeProvider>
        <AuthProvider>
          <UserProvider>
            <NotificationProvider>
              <Elements stripe={stripePromise}>
                <style>
                  {`
                    @keyframes scroll-bg {
                      from { background-position: 0 0; }
                      to { background-position: -100% 0; }
                    }
                  `}
                </style>
                <div
                  className="min-h-screen"
                  style={{
                    backgroundImage: bgStyle,
                    backgroundRepeat: 'repeat-x',
                      backgroundPosition: 'center center',
                      backgroundAttachment: 'fixed',
                    backgroundSize: 'auto 100%',
                    animation: 'scroll-bg 120s linear infinite'
                  }}
                >
                  <AppRoutes />
                </div>
              </Elements>
            </NotificationProvider>
          </UserProvider>
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
